import React, { useContext } from 'react';
import { Route, Redirect, Switch } from 'react-router-dom';
import { QuestionnaireModalProvider } from './utils/questionnaire_modal_context';
import { renderHeader } from './helpers/header_helper';
import { getPublicOnlyRedirectUrl, needsPhoneVerification } from './utils/userRedirect';
import { isFeatureEnabled, FEATURES } from './helpers/feature_checker';

import PreSignupPicker from './components/marketing/join/pre_signup_picker';
import Context from './utils/context';
import Footer from './components/chrome/footer';
import Profile from './components/profile';
import Home from './containers/home';
import PostConversationQuestionnaire from './components/questionnaire/post_conversation_survey/index.jsx';
import BylvayQuestionnaire from './components/questionnaire/questionnaires/bylvay/index_gated.jsx';
import DynamicQuestionnaire from './components/questionnaire/dynamic/index.jsx';
import QuestionnaireModal from './components/ui_kit/modals/questionnaire_modal';

let path = window.location.pathname;

const AuthenticatedRoute = ({ component: Component, path, auth }) => {
  const returnTo = window.location.pathname;
  const pathname = `/member/login?return_to=${encodeURIComponent(returnTo)}`;

  if (auth.isLoggedIn()) {
    const currentUser = auth.getCurrentUser();
    if (needsPhoneVerification(currentUser, { impersonating: auth.isImpersonating() })) {
      return <Route path={path} render={() => <Redirect to='/member/verify-phone' />} />;
    }

    return <Route path={path} render={(props) => <Component {...props} />} />;
  }

  return <Route path={path} render={(props) => <Redirect to={pathname} />} />;
};

const PublicOnlyRoute = ({ component: Component, auth, path }) => {
  const redirectUrl = auth.isLoggedIn()
    ? getPublicOnlyRedirectUrl(auth.getCurrentUser(), { impersonating: auth.isImpersonating() })
    : '/member/conversations';

  return (
    <Route
      exact
      path={path}
      render={(props) =>
        auth.isLoggedIn() ? <Redirect to={{ pathname: redirectUrl }} /> : <Component path={path} {...props} />
      }
    />
  );
};

export const NewApp = () => {
  const context = useContext(Context);

  const footerBgColor = '#173959';
  const footerColumnHeaderStyles = null;

  const account = context?.auth?.currentAccount ? context.auth.currentAccount() : null;
  const hideFooter =
    isFeatureEnabled(account, FEATURES.NEW_UI) ||
    path.startsWith('/member/conversation-survey') ||
    path.startsWith('/member/questionnaires');

  return (
    <>
      {renderHeader(context.auth)}

      <QuestionnaireModalProvider>
        <QuestionnaireModal />
        <Switch>
          <PublicOnlyRoute exact auth={context.auth} path='/' component={Home} />

          {/* Questionnaires  */}
          <AuthenticatedRoute
            auth={context.auth}
            path={`/member/mentor-matches/:id/bylvay`}
            component={BylvayQuestionnaire}
          />
          {/* Builder-driven questionnaires and post surveys (Use Questionnaire Builder feature) */}
          <AuthenticatedRoute
            auth={context.auth}
            path={[`/member/questionnaires/:uuid`, `/member/questionnaires`]}
            component={DynamicQuestionnaire}
          />
          {/* End Questionnaires  */}

          {/* Signup form */}
          <PublicOnlyRoute exact auth={context.auth} path={`/member/join/pre-signup`} component={PreSignupPicker} />
          {/* End signup form */}

          {/* Redirects */}
          <Route path={`/member/join/support`} render={() => <Redirect to='/member/join/pre-signup' />} />
          <Route path={`/member/join/b/doctor-connect`} render={() => <Redirect to='/member/join/pre-signup' />} />
          <Route path='/member/join/surgeon-match' render={() => <Redirect to='/member/join/pre-signup' />} />
          <Route path={`/member/mentor-match`} render={() => <Redirect to='/member/join/pre-signup' />} />
          <PublicOnlyRoute
            exact
            auth={context.auth}
            path={`/member/join`}
            render={() => <Redirect to='/member/join/pre-signup' />}
          />
          {/* End redirects */}

          {/* Profile */}
          <Route path={`/member/profile/:id`} component={Profile} />
          <Route path={`/member/profile/:id/:view(about-me)`} component={Profile} />
          {/* End profile */}

          {/* Conversation survey */}
          <Route path={`/member/conversation-survey/:conversationSurveyId`} component={PostConversationQuestionnaire} />
          {/* End conversation survey */}

          {/* Catchall */}
          <Route path='*' component={Home} />
          {/* End catchall */}
        </Switch>
      </QuestionnaireModalProvider>

      {!hideFooter && (
        <Footer
          columnHeaderStyles={footerColumnHeaderStyles}
          bgColor={footerBgColor}
          fullFooter={path && !path.startsWith('/member') && !context.auth.isLoggedIn()}
        />
      )}
    </>
  );
};
